import { useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { featureFlags, resolveFeatureFlagState } from '../../config/featureFlags';
import { useApi } from '../../hooks/useApi';
import {
  dismissOutcomeSignal,
  listOutcomeInstruments,
  listOutcomeMeasurements,
  listOutcomeSignals,
  reviewOutcomeSignal,
  type CountMeta,
  type OutcomeMeasurementsFilters,
  type OutcomeMeasurementWithJoins,
  type OutcomeSignalsMeta,
} from '../../lib/api/regenloop';
import type { OutcomeInstrument, OutcomeSignal } from '@lineageatlas/shared';
import {
  ConsoleBarList,
  ConsoleBarRow,
  ConsoleButton,
  ConsoleChip,
  ConsoleLegend,
  ConsolePanel,
  ConsolePanelHeader,
  ConsoleRing,
  ConsoleStage,
  ConsoleStageStrip,
  ConsoleTab,
  ConsoleTable,
  ConsoleTabs,
} from './primitives';
import {
  STAGES,
  adverseEvents,
  aeById,
  aesForLot,
  aesForPatient,
  auditEntries,
  compareRows,
  coursesFor,
  investigatorById,
  investigators,
  lotById,
  lots as prototypeLots,
  panelFor,
  patientById,
  patients,
  patientsByLot,
  patientsByProvider,
  patientsByStudy,
  providerById,
  providers,
  signalById,
  signals as prototypeSignals,
  studies,
  studiesByInvestigator,
  studyByCode,
  zoneColors,
  type AuditEntryRecord,
  type LotLineage,
  type PatientRecord,
  type QcPanel,
  type SignalRecord,
  type StatusTone,
} from './data';

type IconName =
  | 'activity'
  | 'alert'
  | 'arrow'
  | 'book'
  | 'capture'
  | 'check'
  | 'clock'
  | 'compare'
  | 'download'
  | 'eye'
  | 'filter'
  | 'flask'
  | 'history'
  | 'learn'
  | 'lot'
  | 'measure'
  | 'patient'
  | 'physician'
  | 'plus'
  | 'provider'
  | 'search'
  | 'settings'
  | 'shield'
  | 'source'
  | 'study'
  | 'target';

interface ApiLot {
  id: string;
  lot_number?: string;
  name?: string;
  status?: string;
  cell_type?: string;
  supplier?: string | null;
  vial_count?: number | null;
  vials_remaining?: number | null;
  latest_qc_result?: string | null;
  current_passage?: number | null;
  collection_date?: string | null;
}

interface ApiAuditEntry {
  id?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  user_name?: string;
  user_email?: string;
  created_at?: string;
}

interface ApiPatient {
  id: string;
  patient_code?: string;
  indication?: string;
  clinic_id?: string;
  created_at?: string;
}

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    activity: <path d="M4 13h4l2-6 4 10 2-4h4" />,
    alert: (
      <>
        <path d="M12 3 2.8 20h18.4L12 3Z" />
        <path d="M12 8v5M12 17h.01" />
      </>
    ),
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    book: (
      <>
        <path d="M5 4h8a3 3 0 0 1 3 3v13H8a3 3 0 0 0-3-3V4Z" />
        <path d="M16 7h3v13h-3" />
      </>
    ),
    capture: (
      <>
        <path d="M5 7V5h2M17 5h2v2M19 17v2h-2M7 19H5v-2" />
        <circle cx="12" cy="12" r="3.5" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    compare: <path d="M5 7h11M5 17h14M14 4l3 3-3 3M10 14l-3 3 3 3" />,
    download: <path d="M12 4v10M8 10l4 4 4-4M5 20h14" />,
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.6" />
      </>
    ),
    filter: <path d="M4 6h16M7 12h10M10 18h4" />,
    flask: <path d="M9 3h6M10 3v5l-5 9a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 17l-5-9V3" />,
    history: (
      <>
        <path d="M4 12a8 8 0 1 0 2.4-5.7L4 8.7" />
        <path d="M4 4v4h4M12 8v5l3 2" />
      </>
    ),
    learn: <path d="M12 3v18M5 7h14M6 16c2.2 0 3.8.6 6 2 2.2-1.4 3.8-2 6-2V6c-2.2 0-3.8.6-6 2-2.2-1.4-3.8-2-6-2v10Z" />,
    lot: <path d="M4 7 12 3l8 4v10l-8 4-8-4V7ZM12 12l8-5M12 12v9M12 12 4 7" />,
    measure: <path d="M4 19V5M4 19h16M8 16v-5M12 16V8M16 16v-8" />,
    patient: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 21a7 7 0 0 1 14 0" />
      </>
    ),
    physician: (
      <>
        <path d="M8 3v5a4 4 0 0 0 8 0V3" />
        <path d="M12 12v3a4 4 0 0 0 8 0v-1" />
        <circle cx="20" cy="13" r="1.5" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    provider: <path d="M4 20V8l8-4 8 4v12M8 20v-7h8v7M9 9h.01M15 9h.01" />,
    search: <path d="m21 21-4.3-4.3M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" />,
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19 12a7.6 7.6 0 0 0-.1-1l2-1.6-2-3.5-2.5 1a7.3 7.3 0 0 0-1.7-1L14.3 3h-4.6l-.4 2.9a7.3 7.3 0 0 0-1.7 1l-2.5-1-2 3.5 2 1.6a7.6 7.6 0 0 0 0 2l-2 1.6 2 3.5 2.5-1a7.3 7.3 0 0 0 1.7 1l.4 2.9h4.6l.4-2.9a7.3 7.3 0 0 0 1.7-1l2.5 1 2-3.5-2-1.6c.1-.3.1-.7.1-1Z" />
      </>
    ),
    shield: <path d="M12 3 19 6v5c0 4.5-2.8 8.1-7 10-4.2-1.9-7-5.5-7-10V6l7-3Z" />,
    source: <path d="M12 3c4 3 6 6 6 10a6 6 0 0 1-12 0c0-4 2-7 6-10Z" />,
    study: <path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" />,
    target: (
      <>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function routeFor(go?: string | null): string {
  if (!go) return '/console';
  if (go === 'home') return '/console';
  if (go === 'registry') return '/console/outcomes-registry';
  if (go === 'lots') return '/console/lots';
  if (go === 'studies') return '/console/studies';
  if (go === 'providers') return '/console/providers';
  if (go === 'investigators') return '/console/investigators';
  if (go === 'intel') return '/console/intelligence';
  if (go === 'compare') return '/console/compare';
  if (go === 'consent') return '/console/consent-access';
  if (go === 'audit') return '/console/audit';
  if (go === 'adverse') return '/console/adverse-events';
  if (go === 'settings') return '/console/settings';
  if (go === 'settings/users') return '/console/settings/team-access';
  if (go.startsWith('patient/')) return `/console/outcomes-registry/${go.slice(8)}`;
  if (go.startsWith('lot/')) return `/console/lots/${go.slice(4)}`;
  if (go.startsWith('study/')) return `/console/studies/${go.slice(6)}`;
  if (go.startsWith('provider/')) return `/console/providers/${go.slice(9)}`;
  if (go.startsWith('investigator/')) return `/console/investigators/${go.slice(13)}`;
  if (go.startsWith('signal/')) return `/console/intelligence/signals/${go.slice(7)}`;
  if (go.startsWith('ae/')) return `/console/adverse-events/${go.slice(3)}`;
  if (go.startsWith('audit/')) return `/console/audit?filter=${go.slice(6)}`;
  if (go.startsWith('adverse/')) return `/console/adverse-events?filter=${go.slice(8)}`;
  return '/console';
}

function GoRow({
  go,
  children,
  className,
  as = 'div',
}: {
  go?: string;
  children: ReactNode;
  className?: string;
  as?: 'tr' | 'div';
}) {
  const navigate = useNavigate();
  const onClick = () => navigate(routeFor(go));
  const rowLabel = go ? `Open ${go}` : 'Open console row';
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick();
  };

  if (as === 'tr') {
    return (
      <tr
        aria-label={rowLabel}
        data-go={go}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
      >
        {children}
      </tr>
    );
  }

  return (
    <div
      aria-label={rowLabel}
      className={className}
      data-go={go}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
    >
      {children}
    </div>
  );
}

function StatusChip({ tone, children }: { tone?: StatusTone; children: ReactNode }) {
  return <ConsoleChip variant={tone}>{children}</ConsoleChip>;
}

function Tile({ icon, color, size = 30 }: { icon: IconName; color: string; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 9,
        display: 'grid',
        placeItems: 'center',
        border: '1px solid var(--hair)',
        background: `color-mix(in oklab, ${color}, transparent 84%)`,
        color,
        flex: 'none',
      }}
    >
      <span style={{ width: size * 0.52, height: size * 0.52 }}>
        <Icon name={icon} />
      </span>
    </span>
  );
}

function Kpi({
  icon,
  label,
  value,
  suffix,
  trend,
  detail,
}: {
  icon: IconName;
  label: string;
  value: string;
  suffix?: string;
  trend?: string;
  detail: string;
}) {
  return (
    <ConsolePanel className="rl-kpi">
      <div className="lbl">
        <Icon name={icon} />
        {label}
      </div>
      <div className="v num">
        {value}
        {suffix ? <span>{suffix}</span> : null}
      </div>
      <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 9, fontFamily: 'var(--mono)' }}>
        {trend ? `${trend} - ` : null}
        {detail}
      </div>
    </ConsolePanel>
  );
}

function Progress({
  value,
  color = 'var(--accent)',
  className,
  style,
}: {
  value: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={`rl-prog ${className ?? ''}`} style={style}>
      <span style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
    </div>
  );
}

function Spark({ values, color = 'var(--accent)' }: { values: number[]; color?: string }) {
  const points = values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * 92 + 4;
      const y = 28 - value * 22;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="rl-spark" viewBox="0 0 100 32" aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function LineChart({
  series,
  labels,
  height = 200,
}: {
  series: Array<{ values: number[]; color: string; dashed?: boolean; area?: boolean; name: string }>;
  labels: string[];
  height?: number;
}) {
  const width = 600;
  const pad = 18;
  const toPoint = (value: number, index: number, len: number) => {
    const x = pad + (index / Math.max(len - 1, 1)) * (width - pad * 2);
    const y = height - pad - value * (height - pad * 2);
    return [x, y] as const;
  };

  return (
    <div>
      <div className="rl-lc" style={{ height }}>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Outcome pattern chart">
          {[0.25, 0.5, 0.75].map((line) => (
            <line
              key={line}
              x1={pad}
              x2={width - pad}
              y1={height - pad - line * (height - pad * 2)}
              y2={height - pad - line * (height - pad * 2)}
              stroke="var(--hair)"
              strokeWidth="1"
            />
          ))}
          {series.map((item) => {
            const points = item.values.map((value, index) => toPoint(value, index, item.values.length));
            const d = points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
            const first = points[0];
            const last = points[points.length - 1];
            if (!first || !last) return null;
            const area = `${d} L ${last[0]} ${height - pad} L ${first[0]} ${height - pad} Z`;
            return (
              <g key={item.name}>
                {item.area ? <path d={area} fill={item.color} opacity="0.11" /> : null}
                <path
                  d={d}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="3"
                  strokeDasharray={item.dashed ? '7 7' : undefined}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {points.map(([x, y], index) => (
                  <circle key={index} cx={x} cy={y} r="3.6" fill={item.color} />
                ))}
              </g>
            );
          })}
        </svg>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          color: 'var(--faint)',
          marginTop: 6,
        }}
      >
        {labels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function MiniAvatar({ initials }: { initials: string }) {
  return <span className="rl-mini-av">{initials}</span>;
}

function LinkPill({ to, color, children }: { to: string; color: string; children: ReactNode }) {
  return (
    <Link className="rl-pill" to={to}>
      <span className="d" style={{ background: color }} />
      {children}
    </Link>
  );
}

function SafetyDisclaimer({ strong = false }: { strong?: boolean }) {
  return (
    <ConsolePanel
      className="rl-disc"
      style={{ padding: '14px 18px', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}
    >
      <Icon name="shield" />
      <span>
        Signals to investigate are research and quality-support prompts. Candidate signal. Outcome pattern. Requires review. Not an efficacy claim.
        {strong ? ' Co-pilot output cannot set, release, or hold lot status.' : null}
      </span>
    </ConsolePanel>
  );
}

function PreviewNote({ children }: { children: ReactNode }) {
  return (
    <div className="rl-preview-note">
      <Icon name="target" />
      <div>{children}</div>
    </div>
  );
}

function Tier({ n }: { n: 1 | 2 | 3 }) {
  return (
    <span className={`rl-tier t${n}`}>
      <span className="dt" />
      Tier {n}
    </span>
  );
}

function normalizeApiLot(api: ApiLot, index: number): LotLineage {
  const base = prototypeLots[index % prototypeLots.length] ?? prototypeLots[0]!;
  const lotNumber = api.lot_number ?? api.name ?? api.id;
  const digits = /\d+/.exec(lotNumber)?.[0] ?? api.id;
  return {
    ...base,
    id: digits,
    line: lotNumber,
    type: api.cell_type ?? base.type,
    source: api.supplier ?? base.source,
    units: api.vial_count ?? base.units,
    doses: api.vials_remaining ?? base.doses,
    status:
      api.status === 'quarantined'
        ? 'warn'
        : api.latest_qc_result === 'fail'
          ? 'neg'
          : api.latest_qc_result === 'warning'
            ? 'warn'
            : base.status,
    statusLbl: api.status?.replace(/_/g, ' ') ?? base.statusLbl,
    released: api.collection_date ? new Date(api.collection_date).toLocaleDateString() : base.released,
    passage: api.current_passage ? `P${api.current_passage}` : base.passage,
  };
}

function normalizeApiPatient(api: ApiPatient, index: number): PatientRecord {
  const base = patients[index % patients.length] ?? patients[0]!;
  const code = api.patient_code ?? api.id;
  return {
    ...base,
    name: code,
    pid: api.id,
    initials: code
      .split(/[-_\s]/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || base.initials,
    indication: api.indication ?? base.indication,
    enrolled: api.created_at ? new Date(api.created_at).toLocaleDateString() : base.enrolled,
  };
}

type AsyncLiveState<T, M = undefined> = {
  data: T | null;
  meta: M | null;
  loading: boolean;
  error: unknown;
  refresh: () => void;
};

type LiveOutcomeData = {
  instruments: OutcomeInstrument[];
  measurements: OutcomeMeasurementWithJoins[];
  instrumentMeta?: CountMeta;
  measurementMeta?: CountMeta;
};

type LiveSignalData = {
  signals: OutcomeSignal[];
  meta?: OutcomeSignalsMeta;
};

const confidenceScores: Record<string, number> = {
  low: 35,
  medium: 60,
  high: 85,
};

function readLegacySignalText(signal: OutcomeSignal, key: string): string | undefined {
  const value = (signal as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

function readLegacySignalConfidence(signal: OutcomeSignal): number | undefined {
  const value = (signal as unknown as Record<string, unknown>).confidence;
  return typeof value === 'number' ? Math.round(value * 100) : undefined;
}

function signalConfidenceScore(signal: OutcomeSignal): number {
  return readLegacySignalConfidence(signal) ?? confidenceScores[signal.confidence] ?? 50;
}

function signalDriver(signal: OutcomeSignal): string {
  if (signal.signal_type === 'missing_followup') return 'Missing follow-up';
  if (signal.signal_type === 'outlier') return 'Outlier outcome pattern';
  if (signal.signal_type === 'stable_noisy') return 'Stable noisy outcome pattern';
  if (signal.signal_type === 'trajectory_change') return 'Trajectory change';
  const legacyDrivers = (signal as unknown as Record<string, unknown>).drivers;
  return Array.isArray(legacyDrivers) && typeof legacyDrivers[0] === 'string'
    ? legacyDrivers[0]
    : 'Outcome pattern';
}

function normalizeLiveSignal(signal: OutcomeSignal, index: number): SignalRecord {
  const description = signal.description ?? readLegacySignalText(signal, 'summary') ?? 'Candidate signal requires review.';
  const evidence = signal.evidence_summary ?? description;
  const investigationCopy = signal.investigation_copy ?? 'Requires review. Not an efficacy claim.';

  return {
    id: signal.id,
    rank: index + 1,
    title: signal.title,
    driver: signalDriver(signal),
    desc: description,
    conf: signalConfidenceScore(signal),
    n: 0,
    lot: null,
    study: 'Live',
    detail: `${evidence} ${investigationCopy}`,
    status: signal.status,
  };
}

function useLiveOutcomeData(enabled: boolean, filters: OutcomeMeasurementsFilters = {}): AsyncLiveState<LiveOutcomeData, CountMeta> {
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<AsyncLiveState<LiveOutcomeData, CountMeta>>({
    data: null,
    meta: null,
    loading: false,
    error: null,
    refresh: () => setVersion((current) => current + 1),
  });
  const patientId = filters.patient_id;
  const assignmentId = filters.assignment_id;
  const instrumentId = filters.instrument_id;

  useEffect(() => {
    if (!enabled) {
      setState((current) => ({ ...current, data: null, meta: null, loading: false, error: null }));
      return;
    }

    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: null }));
    void Promise.all([
      listOutcomeInstruments(),
      listOutcomeMeasurements({ patient_id: patientId, assignment_id: assignmentId, instrument_id: instrumentId }),
    ])
      .then(([instruments, measurements]) => {
        if (cancelled) return;
        setState((current) => ({
          ...current,
          data: {
            instruments: instruments.data,
            measurements: measurements.data,
            instrumentMeta: instruments.meta,
            measurementMeta: measurements.meta,
          },
          meta: measurements.meta ?? null,
          loading: false,
          error: null,
        }));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState((current) => ({ ...current, data: null, meta: null, loading: false, error }));
      });

    return () => {
      cancelled = true;
    };
  }, [assignmentId, enabled, instrumentId, patientId, version]);

  return state;
}

function useLiveSignals(enabled: boolean): AsyncLiveState<LiveSignalData, OutcomeSignalsMeta> & {
  reviewingId: string | null;
  dismissingId: string | null;
  actionError: unknown;
  review: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
} {
  const [version, setVersion] = useState(0);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [state, setState] = useState<AsyncLiveState<LiveSignalData, OutcomeSignalsMeta>>({
    data: null,
    meta: null,
    loading: false,
    error: null,
    refresh: () => setVersion((current) => current + 1),
  });

  useEffect(() => {
    if (!enabled) {
      setState((current) => ({ ...current, data: null, meta: null, loading: false, error: null }));
      return;
    }

    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: null }));
    void listOutcomeSignals()
      .then((response) => {
        if (cancelled) return;
        setState((current) => ({
          ...current,
          data: { signals: response.data, meta: response.meta },
          meta: response.meta ?? null,
          loading: false,
          error: null,
        }));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState((current) => ({ ...current, data: null, meta: null, loading: false, error }));
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, version]);

  async function review(id: string) {
    setReviewingId(id);
    setActionError(null);
    try {
      await reviewOutcomeSignal(id);
      setVersion((current) => current + 1);
    } catch (error) {
      setActionError(error);
    } finally {
      setReviewingId(null);
    }
  }

  async function dismiss(id: string) {
    setDismissingId(id);
    setActionError(null);
    try {
      await dismissOutcomeSignal(id, 'Dismissed from console signal review.');
      setVersion((current) => current + 1);
    } catch (error) {
      setActionError(error);
    } finally {
      setDismissingId(null);
    }
  }

  return { ...state, reviewingId, dismissingId, actionError, review, dismiss };
}

function normalizeAudit(entry: ApiAuditEntry, index: number): AuditEntryRecord {
  const actor = entry.user_name ?? entry.user_email ?? 'System';
  return {
    id: entry.id ?? `live-${index}`,
    at: entry.created_at ? new Date(entry.created_at).toLocaleString() : 'Live audit',
    actor,
    role: 'Workspace user',
    initials: actor
      .split(/[ @.]/)
      .filter(Boolean)
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase(),
    verb: entry.action ?? 'Recorded action',
    target: [entry.entity_type, entry.entity_id].filter(Boolean).join(' - '),
    kind: 'edit',
    status: 'ok',
  };
}

function formatOutcomeMeasurementValue(measurement: OutcomeMeasurementWithJoins): string {
  if (measurement.value_numeric != null) {
    const suffix = measurement.instrument_code ? ` ${measurement.instrument_code}` : '';
    return `${measurement.value_numeric}${suffix}`;
  }
  if (measurement.value_text) return measurement.value_text;
  if (measurement.value_json) return 'structured value';
  return 'value pending';
}

function useConsoleModel() {
  const liveLots = useApi<ApiLot[]>('/api/v1/lots');
  const livePatients = useApi<ApiPatient[]>('/api/v1/patients');
  const liveAudit = useApi<ApiAuditEntry[]>('/api/v1/audit-log?limit=200&offset=0');
  const liveSignals = useLiveSignals(featureFlags.liveSignals);

  return useMemo(
    () => ({
      lots:
        Array.isArray(liveLots.data) && liveLots.data.length > 0
          ? liveLots.data.map(normalizeApiLot)
          : prototypeLots,
      patients:
        Array.isArray(livePatients.data) && livePatients.data.length > 0
          ? livePatients.data.map(normalizeApiPatient)
          : patients,
      signals:
        featureFlags.liveSignals && Array.isArray(liveSignals.data?.signals)
          ? liveSignals.data.signals.map(normalizeLiveSignal)
          : prototypeSignals,
      audit:
        Array.isArray(liveAudit.data) && liveAudit.data.length > 0
          ? liveAudit.data.map(normalizeAudit)
          : auditEntries,
      liveState: {
        lotsLoading: liveLots.loading,
        patientsLoading: livePatients.loading,
        signalsLoading: liveSignals.loading,
        signalsError: liveSignals.error,
        signalsMeta: liveSignals.meta,
        signalsActionError: liveSignals.actionError,
        auditLoading: liveAudit.loading,
      },
      signalActions: {
        review: liveSignals.review,
        dismiss: liveSignals.dismiss,
        reviewingId: liveSignals.reviewingId,
        dismissingId: liveSignals.dismissingId,
      },
    }),
    [
      liveAudit.data,
      liveAudit.loading,
      liveLots.data,
      liveLots.loading,
      livePatients.data,
      livePatients.loading,
      liveSignals.data,
      liveSignals.meta,
      liveSignals.loading,
      liveSignals.error,
      liveSignals.actionError,
      liveSignals.reviewingId,
      liveSignals.dismissingId,
    ],
  );
}

function findLot(liveLots: LotLineage[], id?: string | null) {
  return liveLots.find((lot) => lot.id === id || lot.line === id) ?? lotById(id);
}

function attr(label: string, value: ReactNode, sub?: ReactNode) {
  return (
    <div className="rl-metric" key={label}>
      <div className="k">{label}</div>
      <div className="v num">{value}</div>
      {sub ? <div className="s">{sub}</div> : null}
    </div>
  );
}

export function CommandCenterScreen() {
  const { lots, signals, audit } = useConsoleModel();
  const topSignals = signals.slice(0, 3);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <GoRow go="registry">
          <Kpi icon="patient" label="Active patients" value="1,284" trend="6.2%" detail="vs last quarter" />
        </GoRow>
        <GoRow go="studies">
          <Kpi icon="study" label="Studies running" value={String(studies.length + 1)} trend="2 enrolling" detail="this month" />
        </GoRow>
        <GoRow go="lots">
          <Kpi icon="shield" label="Loop integrity" value="98.2" suffix="%" trend="0.4%" detail="chain-of-custody" />
        </GoRow>
        <GoRow go="intel">
          <Kpi icon="target" label="Signals to investigate" value={String(signals.length)} detail="candidate review queue" />
        </GoRow>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 16 }}>
        <ConsolePanel style={{ display: 'flex', flexDirection: 'column' }}>
          <ConsolePanelHeader title="Closed-loop integrity" muted="live across all parties" right={<StatusChip tone="pos">healthy</StatusChip>} />
          <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 22, padding: 18, flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <ConsoleRing value={<span>98.2%</span>} label="integrity" />
              <div style={{ fontSize: 11, color: 'var(--faint)', textAlign: 'center', fontFamily: 'var(--mono)' }}>
                {lots.length} of {lots.length} lots traceable
              </div>
            </div>
            <div style={{ alignSelf: 'center' }}>
              {[
                ['var(--labs)', 'Labs', 'cell lines', '12', 'lots released', String(lots.length), 100],
                ['var(--phy)', 'Physicians', 'providers', String(providers.length), 'doses logged', '1,284', 96],
                ['var(--pat)', 'Patients', 'check-ins', '3,910', 'follow-up rate', '92%', 92],
              ].map(([color, name, key1, val1, key2, val2, pct]) => (
                <div key={String(name)} style={{ padding: '12px 0', borderBottom: '1px solid var(--hair-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: String(color) }} />
                    <span style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 13 }}>{name}</span>
                    <span className="num" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--faint)' }}>
                      {pct}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 22 }}>
                    <div>
                      <div className="num" style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600 }}>{val1}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--faint)' }}>{key1}</div>
                    </div>
                    <div>
                      <div className="num" style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600 }}>{val2}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--faint)' }}>{key2}</div>
                    </div>
                    <div style={{ flex: 1, alignSelf: 'flex-end' }}>
                      <Progress value={Number(pct)} color={String(color)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ConsolePanel>

        <ConsolePanel style={{ display: 'flex', flexDirection: 'column' }}>
          <ConsolePanelHeader
            title="Signals to investigate"
            muted="candidate review"
            right={<Link className="rl-link" to="/console/intelligence">View all</Link>}
          />
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 11, flex: 1 }}>
            {topSignals.map((signal) => (
              <GoRow key={signal.id} go={`signal/${signal.id}`} className="rl-signal">
                <span className="s-ic"><Icon name="target" /></span>
                <div style={{ minWidth: 0 }}>
                  <div className="s-t">{signal.title}</div>
                  <div className="s-p">{signal.desc}</div>
                  <div className="s-meta">
                    <span>confidence {(signal.conf / 100).toFixed(2)}</span>
                    <span>{signal.n ? `n=${signal.n}` : 'live candidate'}</span>
                  </div>
                </div>
              </GoRow>
            ))}
          </div>
          <div style={{ padding: '0 14px 14px' }}>
            <PreviewNote>Outcome pattern. Requires review. Not an efficacy claim.</PreviewNote>
          </div>
        </ConsolePanel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 16 }}>
        <ConsolePanel style={{ display: 'flex', flexDirection: 'column' }}>
          <ConsolePanelHeader
            title="Cohort outcome patterns"
            muted="6-month PRO trajectory, normalized"
            right={
              <ConsoleLegend
                items={[
                  { label: 'Lot 4471', color: 'var(--accent)' },
                  { label: 'Lot 4482', color: 'var(--violet)' },
                  { label: 'Baseline', color: 'var(--faint)' },
                ]}
              />
            }
          />
          <div style={{ padding: '16px 18px 12px' }}>
            <LineChart
              labels={['Base', 'Wk2', 'Wk6', 'Wk12', 'Mo4', 'Mo5', 'Mo6']}
              series={[
                { name: 'Lot 4471', color: 'var(--accent)', area: true, values: [0.2, 0.3, 0.46, 0.6, 0.72, 0.82, 0.88] },
                { name: 'Lot 4482', color: 'var(--violet)', values: [0.2, 0.27, 0.38, 0.47, 0.55, 0.62, 0.66] },
                { name: 'Baseline', color: 'var(--faint)', dashed: true, values: [0.2, 0.22, 0.25, 0.27, 0.3, 0.32, 0.34] },
              ]}
            />
          </div>
        </ConsolePanel>

        <ConsolePanel style={{ display: 'flex', flexDirection: 'column' }}>
          <ConsolePanelHeader title="Recent loop activity" right={<Link className="rl-link" to="/console/audit">Feed</Link>} />
          <div>
            {audit.slice(0, 5).map((entry) => (
              <GoRow key={entry.id} go={entry.go} className="rl-row">
                <span className="r-ic"><Icon name={entry.kind === 'ae' ? 'alert' : entry.kind === 'release' ? 'lot' : 'activity'} /></span>
                <div>
                  <div className="r-t">{entry.verb}</div>
                  <div className="r-s">{entry.target}</div>
                </div>
                <span className="r-r num" style={{ fontSize: 11, color: 'var(--faint)' }}>{entry.at}</span>
              </GoRow>
            ))}
          </div>
        </ConsolePanel>
      </div>
    </div>
  );
}

export function OutcomesRegistryScreen() {
  const { patients: registryPatients, liveState } = useConsoleModel();
  const liveOutcomes = useLiveOutcomeData(featureFlags.liveOutcomes);
  const outcomeFlagState = resolveFeatureFlagState('liveOutcomes', {
    loading: liveOutcomes.loading,
    error: liveOutcomes.error,
    live: Boolean(liveOutcomes.data),
    fallback: !liveOutcomes.data,
  });
  const instrumentCount = liveOutcomes.data?.instrumentMeta?.count ?? liveOutcomes.data?.instruments.length ?? 0;
  const measurementCount = liveOutcomes.data?.measurementMeta?.count ?? liveOutcomes.data?.measurements.length ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ConsoleTabs>
          <ConsoleTab active>All patients</ConsoleTab>
          <ConsoleTab>Enrolling</ConsoleTab>
          <ConsoleTab>In follow-up</ConsoleTab>
          <ConsoleTab>Overdue</ConsoleTab>
        </ConsoleTabs>
        <ConsoleLegend
          style={{ marginLeft: 'auto' }}
          items={[
            { label: 'Labs', color: 'var(--labs)' },
            { label: 'Physician', color: 'var(--phy)' },
            { label: 'Patient', color: 'var(--pat)' },
          ]}
        />
      </div>
      <PreviewNote>
        {outcomeFlagState.copy.body} Outcome pattern. Requires review. Not an efficacy claim.
      </PreviewNote>
      <ConsolePanel style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <ConsolePanelHeader
          title="Outcomes Registry"
          muted={`${liveState.patientsLoading ? 'Loading' : '1,284'} patients - ${outcomeFlagState.copy.label}`}
          right={
            <>
              <StatusChip tone={outcomeFlagState.state === 'live' ? 'pos' : outcomeFlagState.state === 'loading' ? 'acc' : 'warn'}>
                {outcomeFlagState.copy.label}
              </StatusChip>
              {featureFlags.liveOutcomes ? <StatusChip tone="acc">{instrumentCount} instruments</StatusChip> : null}
              {featureFlags.liveOutcomes ? <StatusChip tone="vio">{measurementCount} measurements</StatusChip> : null}
              <StatusChip tone="acc">1,180 in follow-up</StatusChip>
              <StatusChip tone="warn">14 overdue</StatusChip>
            </>
          }
        />
        <ConsoleTable>
          <thead>
            <tr>
              <th>Patient</th>
              <th>Indication</th>
              <th>Cycle / stage</th>
              <th>Lot</th>
              <th>Outcome index</th>
              <th>6-mo delta</th>
              <th>Trajectory</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {registryPatients.map((patient) => {
              const courses = coursesFor(patient);
              return (
                <GoRow key={patient.pid} as="tr" go={`patient/${patient.pid}`}>
                  <td>
                    <div className="rl-cell-id">
                      <MiniAvatar initials={patient.initials} />
                      <div>
                        <div className="nm">{patient.name}</div>
                        <div className="pid">{patient.pid}</div>
                      </div>
                    </div>
                  </td>
                  <td>{patient.indication}</td>
                  <td>
                    <div className="rl-stagecell">
                      <span className={`rl-cyc${courses.nCourses > 1 ? ' multi' : ''}`}>C{courses.nCourses}</span>
                      <span className="rl-zonetag">
                        <span className="d" style={{ background: zoneColors[patient.zone] }} />
                        {STAGES[patient.stageIdx]}
                      </span>
                      {courses.nCourses > 1 ? <span className="rl-cycn">{courses.totalAdmins} admin</span> : null}
                    </div>
                  </td>
                  <td><span className="num" style={{ color: 'var(--dim)' }}>#{patient.lot}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Progress value={patient.idx} className={patient.status === 'warn' ? 'vio' : 'pos'} style={{ width: 84 }} />
                      <span className="num" style={{ fontSize: 12 }}>{patient.idx}</span>
                    </div>
                  </td>
                  <td><StatusChip tone={patient.status}>{patient.d}</StatusChip></td>
                  <td><Spark values={patient.sp} color={patient.status === 'warn' ? 'var(--warn)' : patient.status === 'neg' ? 'var(--neg)' : 'var(--pos)'} /></td>
                  <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
                </GoRow>
              );
            })}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

export function PatientDetailScreen() {
  const { id } = useParams();
  const { patients: registryPatients } = useConsoleModel();
  const patient = registryPatients.find((item) => item.pid === id) ?? patientById(id) ?? patients[0]!;
  const liveOutcomes = useLiveOutcomeData(featureFlags.liveOutcomes, { patient_id: patient.pid });
  const outcomeFlagState = resolveFeatureFlagState('liveOutcomes', {
    loading: liveOutcomes.loading,
    error: liveOutcomes.error,
    live: Boolean(liveOutcomes.data),
    fallback: !liveOutcomes.data,
  });
  const liveMeasurements = liveOutcomes.data?.measurements ?? [];
  const liveInstruments = liveOutcomes.data?.instruments ?? [];
  const lot = lotById(patient.lot);
  const study = studyByCode(patient.study);
  const provider = providerById(patient.provider);
  const courseMeta = coursesFor(patient);
  const safetyEvents = aesForPatient(patient.pid);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <span className="rl-avatar" style={{ width: 46, height: 46, fontSize: 16 }}>{patient.initials}</span>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="h-title">{patient.name}</span>
            <StatusChip tone="acc">{courseMeta.nCourses} care episode{courseMeta.nCourses > 1 ? 's' : ''}</StatusChip>
          </div>
          <div className="h-meta">
            {patient.pid} - {patient.age}{patient.sex} - {patient.indication} ({patient.side}) - Study {patient.study} - Cycle {courseMeta.nCourses}, {STAGES[patient.stageIdx] ?? 'Measure'}
          </div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{patient.idx}</div><div className="l">Outcome index</div></div>
          <div><div className="v num" style={{ color: 'var(--pos)' }}>{patient.d}</div><div className="l">Latest KOOS delta</div></div>
          <div><div className="v num">{courseMeta.totalAdmins}</div><div className="l">Administrations</div></div>
          <div><div className="v num">{patient.days}</div><div className="l">Days in loop</div></div>
        </div>
      </ConsolePanel>

      <div className="rl-pillrow">
        {courseMeta.lotSet.map((lotId) => (
          <LinkPill key={lotId} to={`/console/lots/${lotId}`} color="var(--labs)">Lot #{lotId} {lotById(lotId)?.line}</LinkPill>
        ))}
        <LinkPill to={`/console/studies/${patient.study}`} color="var(--accent)">Study {patient.study} {study?.name}</LinkPill>
        <LinkPill to={`/console/providers/${patient.provider}`} color="var(--phy)">{provider?.name}</LinkPill>
        {safetyEvents.length ? (
          <LinkPill to={`/console/adverse-events/${safetyEvents[0]?.id ?? ''}`} color="var(--neg)">
            {safetyEvents.length} adverse event{safetyEvents.length > 1 ? 's' : ''}
          </LinkPill>
        ) : (
          <LinkPill to="/console/adverse-events" color="var(--neg)">Report adverse event</LinkPill>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Active loop stage" muted={lot ? `Lot #${lot.id} - ${lot.line}` : undefined} />
          <div style={{ padding: 16 }}>
            <ConsoleStageStrip>
              {STAGES.map((stage, index) => (
                <ConsoleStage
                  key={stage}
                  number={String(index + 1)}
                  name={stage}
                  date={index <= patient.stageIdx ? 'logged' : 'pending'}
                  complete={index < patient.stageIdx}
                  current={index === patient.stageIdx}
                  icon={<Icon name={index < 2 ? 'source' : index < 4 ? 'provider' : index === 4 ? 'measure' : 'learn'} />}
                />
              ))}
            </ConsoleStageStrip>
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader
            title="Outcome trajectory"
            muted={outcomeFlagState.state === 'live' ? 'live measurements loaded' : outcomeFlagState.copy.label}
            right={
              featureFlags.liveOutcomes ? (
                <StatusChip tone={outcomeFlagState.state === 'live' ? 'pos' : outcomeFlagState.state === 'loading' ? 'acc' : 'warn'}>
                  {outcomeFlagState.copy.label}
                </StatusChip>
              ) : null
            }
          />
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <LineChart
              height={180}
              labels={['Base', 'Wk2', 'Wk6', 'Wk12', 'Mo4', 'Mo6']}
              series={[
                { name: patient.name, color: 'var(--accent)', area: true, values: patient.sp },
                { name: 'Study mean', color: 'var(--faint)', dashed: true, values: [0.25, 0.31, 0.42, 0.52, 0.59, 0.64] },
              ]}
            />
            {featureFlags.liveOutcomes ? (
              <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 12 }}>
                <div className="rl-seclbl">Live outcome measurements</div>
                {liveMeasurements.length ? (
                  liveMeasurements.slice(0, 3).map((measurement) => (
                    <div className="rl-row" key={measurement.id}>
                      <span className="r-ic"><Icon name="measure" /></span>
                      <div>
                        <div className="r-t">{measurement.instrument_name}</div>
                        <div className="r-s">
                          {measurement.timepoint_label ?? new Date(measurement.measured_at).toLocaleDateString()} - {formatOutcomeMeasurementValue(measurement)}
                        </div>
                      </div>
                      <span className="r-r"><StatusChip tone="acc">{measurement.source}</StatusChip></span>
                    </div>
                  ))
                ) : (
                  <PreviewNote>
                    {outcomeFlagState.copy.body} {liveInstruments.length} instrument{liveInstruments.length === 1 ? '' : 's'} available.
                  </PreviewNote>
                )}
              </div>
            ) : null}
          </div>
        </ConsolePanel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Care episodes" muted="longitudinal care model" />
          <div style={{ padding: 16 }}>
            {courseMeta.courses.slice().reverse().map((course) => (
              <div className={`rl-course${course.active ? ' on' : ''}`} style={{ '--cc': course.color } as CSSProperties} key={course.label}>
                <div className="cc-body">
                  <div className="cc-main">
                    <div className="cc-head">
                      <span className="cc-no">{course.label.toUpperCase()}</span>
                      <span className="cc-kind">{course.kind}</span>
                      <StatusChip tone={course.active ? 'acc' : 'pos'}>{course.active ? `Active - ${STAGES[Math.min(course.stageIdx, 5)]}` : 'Complete'}</StatusChip>
                    </div>
                    <div className="cc-meta">Lot #{course.lot} - {course.line} - {course.type} - {course.side} side - {course.injections.length} administration{course.injections.length > 1 ? 's' : ''}</div>
                    <div className="cc-reason">{course.reason}</div>
                    <div className="cc-stats">
                      <div><div className="v num">{course.peakLbl}</div><div className="l">{course.active ? 'Outcome index' : 'Peak index'}</div></div>
                      <div><div className="v num" style={{ color: 'var(--pos)' }}>{course.koos}</div><div className="l">KOOS delta</div></div>
                      <div><div className="v num">{course.startDate}</div><div className="l">{course.active ? 'Started' : 'Window'}</div></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Follow-up log" muted="audit-linked" />
          <div>
            {[
              ['Day 90 - PRO check-in', 'Auto-questionnaire completed', 'pos', 'Done'],
              ['Day 60 - clinician note', `${provider?.lead ?? 'Clinician'} review`, 'pos', 'Done'],
              ['Day 30 - PRO check-in', `KOOS ${patient.d}`, 'pos', 'Done'],
              ['Day 14 - safety check', safetyEvents.length ? `${safetyEvents.length} event linked` : 'No adverse events', safetyEvents.length ? 'warn' : 'pos', 'Done'],
              ['6-mo endpoint', 'Scheduled', 'warn', 'Upcoming'],
            ].map(([title, sub, tone, right]) => (
              <div className="rl-row" key={title}>
                <span className="r-ic"><Icon name={tone === 'warn' ? 'clock' : 'check'} /></span>
                <div><div className="r-t">{title}</div><div className="r-s">{sub}</div></div>
                <span className="r-r"><StatusChip tone={tone as StatusTone}>{right}</StatusChip></span>
              </div>
            ))}
          </div>
        </ConsolePanel>
      </div>
    </div>
  );
}

export function LotsScreen() {
  const { lots } = useConsoleModel();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="lot" label="Lots released" value={String(lots.length)} detail="across 3 cell lines" />
        <Kpi icon="shield" label="Traceable" value="100" suffix="%" detail="chain-of-custody" />
        <Kpi icon="measure" label="Doses administered" value="247" detail="all unit-traced" />
        <Kpi icon="target" label="Review lead" value="#4471" detail="candidate pattern" />
      </div>
      <ConsolePanel style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <ConsolePanelHeader title="Lot Lineage" muted={`${lots.length} lots - origin to outcome`} right={<StatusChip tone="pos">all traceable</StatusChip>} />
        <ConsoleTable>
          <thead>
            <tr><th>Lot</th><th>Tissue</th><th>Released</th><th>Study</th><th>Units</th><th>Doses</th><th>Outcome index</th><th>vs mean</th><th /></tr>
          </thead>
          <tbody>
            {lots.map((lot) => (
              <GoRow as="tr" go={`lot/${lot.id}`} key={lot.id}>
                <td><div className="rl-cell-id"><Tile icon="lot" color="var(--labs)" /><div><div className="nm">Lot #{lot.id}</div><div className="pid">{lot.line} - {lot.type}</div></div></div></td>
                <td>{lot.source}</td>
                <td><span className="num" style={{ color: 'var(--dim)' }}>{lot.released}</span></td>
                <td><Link className="rl-zonetag" to={`/console/studies/${lot.study}`}><span className="d" style={{ background: 'var(--accent)' }} />{lot.study}</Link></td>
                <td className="num">{lot.units}</td>
                <td className="num">{lot.doses}</td>
                <td>{lot.respIndex ? <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Progress value={lot.respIndex} style={{ width: 72 }} /><span className="num" style={{ fontSize: 12 }}>{lot.respIndex}</span></div> : <span style={{ color: 'var(--faint)' }}>-</span>}</td>
                <td><StatusChip tone={lot.status}>{lot.vsMean}</StatusChip></td>
                <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
              </GoRow>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

function QcCopilot({ panel }: { panel: QcPanel }) {
  return (
    <div className="rl-copilot">
      <div className="rl-copilot-h">
        <span className="rl-copilot-mk"><Icon name="target" /></span>
        <div>
          <div className="ti">QC co-pilot <Tier n={1} /></div>
          <div className="su">Second-review support. Human confirmation required.</div>
        </div>
        <div className="right rl-conf">
          <div className="cl">confidence</div>
          <div className="cbar"><span style={{ width: `${panel.confidence * 100}%` }} /></div>
          <div className="cv">{Math.round(panel.confidence * 100)}%</div>
        </div>
      </div>
      <div className={`rl-risk ${panel.risk}`}>
        <div className="badge"><span className="lvl">{panel.risk}</span><span className="lbl">risk</span></div>
        <div className="sum">{panel.summary}<span className="src">{panel.reviewedBasis}</span></div>
      </div>
      <table className="rl-findings">
        <thead><tr><th>Marker</th><th>Value</th><th>Reference</th><th>Finding</th></tr></thead>
        <tbody>
          {panel.markers.map((marker) => (
            <tr key={marker.name}>
              <td className="mk">{marker.name}<small>{marker.sub}</small></td>
              <td className="val">{marker.value} <span className={`rl-prov ${marker.prov}`}><span className="pd" />{marker.prov}</span></td>
              <td className="ref">{marker.refText}</td>
              <td><span className={`rl-fbadge ${marker.finding}`}><Icon name={marker.finding === 'pass' ? 'check' : 'alert'} />{marker.finding}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="rl-recs">
        {panel.recs.map((rec, index) => (
          <div className="rl-rec" key={rec}><span className="rn">{index + 1}</span><span>{rec}</span></div>
        ))}
      </div>
      <div className="rl-confirm">
        <div className="note"><b>Requires review.</b> Co-pilot output is assistive and cannot set, release, or hold lot status.</div>
        <div className="acts"><ConsoleButton variant="gho">Add review note</ConsoleButton><ConsoleButton variant="pri">Mark reviewed</ConsoleButton></div>
      </div>
    </div>
  );
}

export function LotDetailScreen() {
  const { id } = useParams();
  const { lots } = useConsoleModel();
  const lot = findLot(lots, id) ?? prototypeLots[0]!;
  const study = studyByCode(lot.study);
  const linked = patientsByLot(lot.id);
  const panel = panelFor(lot.id);
  const lotAes = aesForLot(lot.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <Tile icon="lot" color="var(--labs)" size={46} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="h-title">Lot #{lot.id}</span>
            <StatusChip tone={lot.status}>{lot.statusLbl}</StatusChip>
          </div>
          <div className="h-meta">Cell line {lot.line} - {lot.type} - Released {lot.released} - Study {lot.study}</div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{lot.units}</div><div className="l">Units released</div></div>
          <div><div className="v num">{lot.doses}</div><div className="l">Doses administered</div></div>
          <div><div className="v num" style={{ color: lot.status === 'warn' ? 'var(--warn)' : 'var(--pos)' }}>{lot.vsMean}</div><div className="l">vs study mean</div></div>
        </div>
      </ConsolePanel>

      <div className="rl-pillrow">
        <LinkPill to={`/console/studies/${lot.study}`} color="var(--accent)">Study {lot.study} - {study?.name}</LinkPill>
        <LinkPill to="/console/outcomes-registry" color="var(--pat)">{linked.length} linked patients</LinkPill>
        <LinkPill to="/console/compare" color="var(--violet)">Compare with other lots</LinkPill>
        {lotAes.length ? <LinkPill to={`/console/adverse-events/${lotAes[0]?.id ?? ''}`} color="var(--neg)">{lotAes.length} adverse event link</LinkPill> : null}
      </div>

      <div className="rl-drift">
        <span className="d-ic"><Icon name="alert" /></span>
        <div className="d-body">
          <div className="d-head"><span className="d-t">Drift warning requires review</span><StatusChip tone="warn">Requires review</StatusChip></div>
          <div className="d-p">Passage, viability, and outcome-pattern deltas are surfaced together for human review. This is not an efficacy claim.</div>
          <div className="d-prov"><span>measured QC</span><span>imported release record</span><span>reference range</span></div>
        </div>
      </div>

      <QcCopilot panel={panel} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConsolePanel style={{ display: 'flex', flexDirection: 'column' }}>
          <ConsolePanelHeader title="Chain of custody" muted="origin to outcome" />
          <div style={{ padding: '18px 20px' }}>
            {lot.custody.map((row, index) => (
              <div className="rl-m-tlrow" key={`${row[2]}-${row[4]}`}>
                <div className="rl-m-tlrail">
                  <span className="rl-m-tldot on" style={{ '--pat': zoneColors[row[1]], borderColor: zoneColors[row[1]] } as CSSProperties}><Icon name={row[0] === 'source' ? 'source' : row[0] === 'flask' ? 'flask' : row[0] === 'produce' ? 'lot' : row[0] === 'admin' ? 'patient' : 'measure'} /></span>
                  {index === lot.custody.length - 1 ? null : <span className="rl-m-tlline" style={{ background: 'var(--hair)' }} />}
                </div>
                <div className="rl-m-tlbody" style={{ paddingBottom: index === lot.custody.length - 1 ? 0 : 20 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span className="t" style={{ fontSize: 13.5, whiteSpace: 'nowrap' }}>{row[2]}</span>
                    <span className="num" style={{ fontSize: 10.5, color: 'var(--faint)', whiteSpace: 'nowrap' }}>{row[4]}</span>
                  </div>
                  <div className="s" style={{ color: 'var(--dim)' }}>{row[3]}</div>
                </div>
              </div>
            ))}
          </div>
        </ConsolePanel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ConsolePanel>
            <ConsolePanelHeader title="Outcomes linked to this lot" muted="Outcome pattern, not claim" />
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 18, padding: 18, alignItems: 'center' }}>
              <ConsoleRing value={lot.respIndex || '-'} label="index" status={<StatusChip tone={lot.status}>{lot.vsMean}</StatusChip>} />
              <ConsoleBarList>
                <ConsoleBarRow label="Outcome index" value={lot.respIndex || '-'} width={lot.respIndex || 0} />
                <ConsoleBarRow label="6-mo KOOS delta" value={lot.koosDelta} width={lot.respIndex || 0} color="var(--pos)" />
                <ConsoleBarRow label="Follow-up rate" value={`${lot.followup}%`} width={lot.followup || 0} color="var(--pat)" />
              </ConsoleBarList>
            </div>
          </ConsolePanel>
          <ConsolePanel>
            <ConsolePanelHeader title="Provenance and characterization" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, padding: 14 }}>
              {attr('Tissue source', lot.source, lot.type.includes('Auto') ? 'autologous' : 'allogeneic')}
              {attr('Viability', lot.viability, 'at release')}
              {attr('Passage', lot.passage, 'expansion')}
              {attr('Endotoxin', lot.endotoxin, 'within spec')}
            </div>
          </ConsolePanel>
        </div>
      </div>

      <ConsolePanel>
        <ConsolePanelHeader title="Linked patients" muted={`${linked.length} in follow-up`} right={<Link className="rl-link" to="/console/outcomes-registry">Registry</Link>} />
        <ConsoleTable>
          <thead><tr><th>Patient</th><th>Indication</th><th>Stage</th><th>6-mo delta</th><th /></tr></thead>
          <tbody>
            {linked.map((patient) => (
              <GoRow as="tr" go={`patient/${patient.pid}`} key={patient.pid}>
                <td><div className="rl-cell-id"><MiniAvatar initials={patient.initials} /><div><div className="nm">{patient.name}</div><div className="pid">{patient.pid}</div></div></div></td>
                <td>{patient.indication}</td>
                <td><span className="rl-zonetag"><span className="d" style={{ background: zoneColors[patient.zone] }} />{STAGES[patient.stageIdx]}</span></td>
                <td><StatusChip tone={patient.status}>{patient.d}</StatusChip></td>
                <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
              </GoRow>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
      <SafetyDisclaimer strong />
    </div>
  );
}

export function QcDetailScreen() {
  const { id } = useParams();
  const lot = lotById(id ?? '') ?? prototypeLots[0]!;
  const panel = panelFor(lot.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <Tile icon="flask" color="var(--labs)" size={46} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="h-title">QC review - Lot #{lot.id}</span>
            <StatusChip tone={panel.risk === 'high' ? 'neg' : panel.risk === 'med' ? 'warn' : 'pos'}>
              Requires review
            </StatusChip>
          </div>
          <div className="h-meta">{lot.line} - {lot.type} - {lot.source}</div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{lot.viability}</div><div className="l">Viability</div></div>
          <div><div className="v num">{lot.passage}</div><div className="l">Passage</div></div>
          <div><div className="v num">{lot.endotoxin}</div><div className="l">Endotoxin</div></div>
        </div>
      </ConsolePanel>
      <div className="rl-pillrow">
        <LinkPill to={`/console/lots/${lot.id}`} color="var(--labs)">Open lot record</LinkPill>
        <LinkPill to="/console/adverse-events" color="var(--neg)">Safety desk</LinkPill>
        <LinkPill to="/console/compare" color="var(--violet)">Compare lots</LinkPill>
      </div>
      <QcCopilot panel={panel} />
    </div>
  );
}

export function StudiesScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <ConsoleTabs>
        <ConsoleTab active>Studies</ConsoleTab>
        <ConsoleTab>Protocols</ConsoleTab>
        <ConsoleTab>Cohorts</ConsoleTab>
        <ConsoleTab>Enrollment</ConsoleTab>
      </ConsoleTabs>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16, flex: 1, minHeight: 0 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Active studies" muted={`${studies.length} running - 2 enrolling`} />
          <ConsoleTable>
            <thead><tr><th>Study</th><th>Phase</th><th>Cohorts</th><th>Sites</th><th>Enrollment</th><th>Status</th><th /></tr></thead>
            <tbody>
              {studies.map((study) => (
                <GoRow as="tr" go={`study/${study.code}`} key={study.code}>
                  <td><div><div className="nm">{study.name}</div><div className="pid">{study.code}</div></div></td>
                  <td><StatusChip tone="acc">{study.phase}</StatusChip></td>
                  <td className="num">{study.cohorts.length}</td>
                  <td className="num" style={{ color: 'var(--dim)' }}>{study.sites}</td>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: 11 }}><Progress value={(study.enrolled / study.target) * 100} style={{ width: 100 }} /><span className="num" style={{ fontSize: 12 }}>{study.enrolled}/{study.target}</span></div></td>
                  <td><StatusChip tone={study.status}>{study.statusLbl}</StatusChip></td>
                  <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
                </GoRow>
              ))}
            </tbody>
          </ConsoleTable>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Enrollment funnel" muted="all studies" />
          <div style={{ padding: '18px 20px' }}>
            {[
              ['Screened', '1,820', 100, 'var(--deep-4)'],
              ['Eligible', '1,510', 83, 'var(--labs)'],
              ['Consented', '1,350', 74, 'var(--phy)'],
              ['Enrolled', '1,284', 71, 'var(--accent)'],
              ['In follow-up', '1,180', 65, 'var(--pat)'],
            ].map(([label, value, pct, color]) => (
              <div style={{ marginBottom: 13 }} key={String(label)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}><span style={{ color: 'var(--dim)' }}>{label}</span><span className="num">{value}</span></div>
                <div style={{ height: 26, borderRadius: 7, background: 'var(--hair-2)', overflow: 'hidden' }}><div style={{ height: '100%', width: `${pct}%`, borderRadius: 7, background: String(color), display: 'flex', alignItems: 'center', paddingLeft: 10 }}><span className="num" style={{ fontSize: 10.5, color: 'var(--deep)', fontWeight: 600 }}>{pct}%</span></div></div>
              </div>
            ))}
          </div>
        </ConsolePanel>
      </div>
    </div>
  );
}

export function StudyDetailScreen() {
  const { code } = useParams();
  const study = studyByCode(code) ?? studies[0]!;
  const studyPatients = patientsByStudy(study.code);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <Tile icon="study" color="var(--accent)" size={46} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="h-title">{study.name}</span><StatusChip tone={study.status}>{study.statusLbl}</StatusChip></div>
          <div className="h-meta">{study.code} - {study.phase} - {study.sites} sites - PI <Link className="rl-link" to={`/console/investigators/${study.piId}`}>{study.pi}</Link> - Started {study.started}</div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{study.enrolled}</div><div className="l">Enrolled</div></div>
          <div><div className="v num">{study.target}</div><div className="l">Target</div></div>
          <div><div className="v num">{study.cohorts.length}</div><div className="l">Cohorts</div></div>
        </div>
      </ConsolePanel>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Protocol" />
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p className="rl-prose">{study.desc}</p>
            <dl className="rl-kv">
              <dt>Primary endpoint</dt><dd>{study.primary}</dd>
              <dt>Secondary</dt><dd>{study.secondary}</dd>
              <dt>Principal investigator</dt><dd><Link className="rl-link" to={`/console/investigators/${study.piId}`}>{study.pi}</Link></dd>
              <dt>Sites</dt><dd>{study.sites} active</dd>
            </dl>
            <div>
              <div className="rl-seclbl">Product lots in this study</div>
              <div className="rl-pillrow">
                {study.lots.length ? study.lots.map((lotId) => <LinkPill key={lotId} to={`/console/lots/${lotId}`} color="var(--labs)">Lot #{lotId} {lotById(lotId)?.line}</LinkPill>) : <span style={{ color: 'var(--faint)', fontSize: 12 }}>No lots assigned yet</span>}
              </div>
            </div>
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Enrollment" muted={`${Math.round((study.enrolled / study.target) * 100)}% of target`} />
          <div style={{ padding: '18px 20px', textAlign: 'center' }}>
            <ConsoleRing value={`${Math.round((study.enrolled / study.target) * 100)}%`} label="enrolled" />
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', marginTop: 16 }}>
              <div><div className="num" style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600 }}>{study.enrolled}</div><div style={{ fontSize: 10.5, color: 'var(--faint)' }}>enrolled</div></div>
              <div><div className="num" style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600 }}>{study.target - study.enrolled}</div><div style={{ fontSize: 10.5, color: 'var(--faint)' }}>remaining</div></div>
              <div><div className="num" style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 600 }}>{study.sites}</div><div style={{ fontSize: 10.5, color: 'var(--faint)' }}>sites</div></div>
            </div>
          </div>
        </ConsolePanel>
      </div>
      <div>
        <div className="rl-seclbl">Cohorts in {study.code}</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(4, study.cohorts.length)},1fr)`, gap: 14 }}>
          {study.cohorts.map(([name, indication, color, enrolled, target, status, tone]) => (
            <ConsolePanel key={name} style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><span className="rl-zonetag"><span className="d" style={{ background: color }} />{name}</span><span style={{ marginLeft: 'auto' }}><StatusChip tone={tone}>{status}</StatusChip></span></div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{indication}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, margin: '14px 0 8px' }}><span className="num" style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 600 }}>{enrolled}</span><span style={{ fontSize: 11, color: 'var(--faint)' }}>/ {target} enrolled</span></div>
              <Progress value={(enrolled / target) * 100} color={color} />
            </ConsolePanel>
          ))}
        </div>
      </div>
      <PatientTablePanel title="Enrolled patients" patients={studyPatients} empty="No patients enrolled yet" />
    </div>
  );
}

function PatientTablePanel({ title, patients: rows, empty }: { title: string; patients: PatientRecord[]; empty: string }) {
  return (
    <ConsolePanel>
      <ConsolePanelHeader title={title} muted={`${rows.length} shown`} />
      <ConsoleTable>
        <thead><tr><th>Patient</th><th>Indication</th><th>Lot</th><th>Stage</th><th>6-mo delta</th><th /></tr></thead>
        <tbody>
          {rows.length ? rows.map((patient) => (
            <GoRow as="tr" go={`patient/${patient.pid}`} key={patient.pid}>
              <td><div className="rl-cell-id"><MiniAvatar initials={patient.initials} /><div><div className="nm">{patient.name}</div><div className="pid">{patient.pid}</div></div></div></td>
              <td>{patient.indication}</td>
              <td><span className="num" style={{ color: 'var(--dim)' }}>#{patient.lot}</span></td>
              <td><span className="rl-zonetag"><span className="d" style={{ background: zoneColors[patient.zone] }} />{STAGES[patient.stageIdx]}</span></td>
              <td><StatusChip tone={patient.status}>{patient.d}</StatusChip></td>
              <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
            </GoRow>
          )) : <tr><td colSpan={6} style={{ color: 'var(--faint)', textAlign: 'center', padding: 24 }}>{empty}</td></tr>}
        </tbody>
      </ConsoleTable>
    </ConsolePanel>
  );
}

export function ProvidersScreen() {
  const totalPatients = providers.reduce((sum, provider) => sum + provider.patients, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="provider" label="Provider sites" value={String(providers.length)} detail="4 regions" />
        <Kpi icon="patient" label="Patients tracked" value={String(totalPatients)} detail="in active follow-up" />
        <Kpi icon="measure" label="Doses logged" value={String(providers.reduce((sum, p) => sum + p.doses, 0))} detail="all unit-traced" />
        <Kpi icon="target" label="Mean outcome index" value="77" detail="across network" />
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title="Provider Network" muted={`${providers.length} sites - ${totalPatients} patients`} />
        <ConsoleTable>
          <thead><tr><th>Provider</th><th>Type</th><th>Region</th><th>Patients</th><th>Doses</th><th>Outcome index</th><th>Status</th><th /></tr></thead>
          <tbody>
            {providers.map((provider) => (
              <GoRow as="tr" go={`provider/${provider.id}`} key={provider.id}>
                <td><div className="rl-cell-id"><MiniAvatar initials={provider.initials} /><div><div className="nm">{provider.name}</div><div className="pid">{provider.org}</div></div></div></td>
                <td>{provider.type}</td>
                <td>{provider.region}</td>
                <td className="num">{provider.patients}</td>
                <td className="num">{provider.doses}</td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Progress value={provider.respIndex} style={{ width: 72 }} /><span className="num" style={{ fontSize: 12 }}>{provider.respIndex}</span></div></td>
                <td><StatusChip tone={provider.status}>{provider.statusLbl}</StatusChip></td>
                <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
              </GoRow>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

export function ProviderDetailScreen() {
  const { id } = useParams();
  const provider = providerById(id) ?? providers[0]!;
  const sitePatients = patientsByProvider(provider.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <MiniAvatar initials={provider.initials} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="h-title">{provider.name}</span><StatusChip tone={provider.status}>{provider.statusLbl}</StatusChip></div>
          <div className="h-meta">{provider.org} - {provider.type} - {provider.region} - Lead <Link className="rl-link" to={`/console/investigators/${provider.leadId}`}>{provider.lead}</Link></div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{provider.patients}</div><div className="l">Patients</div></div>
          <div><div className="v num">{provider.doses}</div><div className="l">Doses logged</div></div>
          <div><div className="v num">{provider.respIndex}</div><div className="l">Outcome index</div></div>
        </div>
      </ConsolePanel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Site overview" />
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <dl className="rl-kv">
              <dt>Organization</dt><dd>{provider.org}</dd>
              <dt>Specialty</dt><dd>{provider.type}</dd>
              <dt>Region</dt><dd>{provider.region}</dd>
              <dt>Site lead</dt><dd><Link className="rl-link" to={`/console/investigators/${provider.leadId}`}>{provider.lead}</Link></dd>
            </dl>
            <div><div className="rl-seclbl">Lots administered here</div><div className="rl-pillrow">{provider.lots.map((lotId) => <LinkPill key={lotId} to={`/console/lots/${lotId}`} color="var(--labs)">Lot #{lotId} {lotById(lotId)?.line}</LinkPill>)}</div></div>
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Outcome pattern panel" muted="vs network mean (77)" />
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 18, padding: 18, alignItems: 'center' }}>
            <ConsoleRing value={provider.respIndex} label="index" />
            <ConsoleBarList>
              <ConsoleBarRow label="Follow-up rate" value="94%" width={94} color="var(--pat)" />
              <ConsoleBarRow label="Protocol adherence" value={provider.status === 'warn' ? '81%' : '96%'} width={provider.status === 'warn' ? 81 : 96} />
              <ConsoleBarRow label="Data completeness" value="98%" width={98} color="var(--pos)" />
            </ConsoleBarList>
          </div>
        </ConsolePanel>
      </div>
      <PatientTablePanel title="Patients at this site" patients={sitePatients} empty="No patients yet" />
    </div>
  );
}

function investigatorCoverage(id: string) {
  const investigator = investigatorById(id);
  const led = studiesByInvestigator(id);
  const supported = led.length ? led : studies.filter((study) => patients.some((patient) => patient.study === study.code && providerById(patient.provider)?.leadId === id));
  const pts = investigator?.role === 'Principal Investigator'
    ? patients.filter((patient) => supported.some((study) => study.code === patient.study))
    : patients.filter((patient) => providerById(patient.provider)?.leadId === id);
  const mean = pts.length ? Math.round(pts.reduce((sum, patient) => sum + patient.idx, 0) / pts.length) : 0;
  return { supported, pts, mean };
}

export function InvestigatorsScreen() {
  const piCount = investigators.filter((investigator) => investigator.role === 'Principal Investigator').length;
  const sites = new Set(investigators.map((investigator) => investigator.affiliation)).size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="physician" label="Physicians" value={String(investigators.length)} detail="in the network" />
        <Kpi icon="study" label="Running a study" value={String(piCount)} detail="lead investigators" />
        <Kpi icon="measure" label="Studies with a PI" value={String(studies.filter((study) => study.piId).length)} detail={`of ${studies.length} studies`} />
        <Kpi icon="provider" label="Sites covered" value={String(sites)} detail="across the network" />
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title="Physician network" muted={`${investigators.length} physicians - ${piCount} running a study`} />
        <ConsoleTable>
          <thead><tr><th>Physician</th><th>Role</th><th>Site</th><th>Studies</th><th>Patients</th><th>Status</th><th /></tr></thead>
          <tbody>
            {investigators.map((investigator) => {
              const cov = investigatorCoverage(investigator.id);
              const site = providerById(investigator.affiliation);
              return (
                <GoRow as="tr" go={`investigator/${investigator.id}`} key={investigator.id}>
                  <td><div className="rl-cell-id"><MiniAvatar initials={investigator.initials} /><div><div className="nm">{investigator.name}</div><div className="pid">{investigator.cred} - {investigator.specialty}</div></div></div></td>
                  <td><StatusChip tone={investigator.role === 'Principal Investigator' ? 'acc' : undefined}>{investigator.role === 'Principal Investigator' ? 'Principal' : 'Sub-investigator'}</StatusChip></td>
                  <td>{site ? <Link className="rl-zonetag" to={`/console/providers/${site.id}`}><span className="d" style={{ background: 'var(--phy)' }} />{site.name}</Link> : '-'}</td>
                  <td className="num">{cov.supported.length}</td>
                  <td className="num">{cov.pts.length}</td>
                  <td><StatusChip tone={investigator.status}>{investigator.statusLbl}</StatusChip></td>
                  <td className="t-r"><span style={{ color: 'var(--faint)' }}>›</span></td>
                </GoRow>
              );
            })}
          </tbody>
        </ConsoleTable>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--hair)', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}>
          Physicians added here become selectable as care clinician, provider lead, and principal investigator where assigned.
        </div>
      </ConsolePanel>
    </div>
  );
}

export function InvestigatorDetailScreen() {
  const { id } = useParams();
  const investigator = investigatorById(id) ?? investigators[0]!;
  const site = providerById(investigator.affiliation);
  const cov = investigatorCoverage(investigator.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <MiniAvatar initials={investigator.initials} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="h-title">{investigator.name}</span><StatusChip tone={investigator.status}>{investigator.statusLbl}</StatusChip></div>
          <div className="h-meta">{investigator.cred} - {investigator.specialty} - {investigator.region} - NPI {investigator.npi}</div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{cov.supported.length}</div><div className="l">Studies</div></div>
          <div><div className="v num">{cov.pts.length}</div><div className="l">Patients</div></div>
          <div><div className="v num">{cov.mean}</div><div className="l">Mean index</div></div>
        </div>
      </ConsolePanel>
      <div className="rl-pillrow">
        {site ? <LinkPill to={`/console/providers/${site.id}`} color="var(--phy)">{site.name}</LinkPill> : null}
        <LinkPill to="/console/studies" color="var(--accent)">Study Management</LinkPill>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Profile" />
          <div style={{ padding: '18px 20px' }}>
            <dl className="rl-kv">
              <dt>Role</dt><dd>{investigator.role}</dd>
              <dt>Primary site</dt><dd>{site ? <Link className="rl-link" to={`/console/providers/${site.id}`}>{site.name}</Link> : '-'}</dd>
              <dt>Email</dt><dd>{investigator.email}</dd>
              <dt>Focus</dt><dd>{investigator.focus}</dd>
              <dt>Onboarded</dt><dd>{investigator.since}</dd>
            </dl>
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Onboarding and access" muted="least privilege" />
          <div>
            {[
              ['License verified', investigator.license],
              ['GCP training', investigator.gcp],
              ['Disclosure current', investigator.disclosure],
              ['Delegation log', investigator.delegation],
            ].map(([label, ok]) => (
              <div className="rl-row" key={String(label)}>
                <span className="r-ic"><Icon name={ok ? 'check' : 'clock'} /></span>
                <div><div className="r-t">{label}</div><div className="r-s">{ok ? 'Complete' : 'Needs review'}</div></div>
                <span className="r-r"><StatusChip tone={ok ? 'pos' : 'warn'}>{ok ? 'ready' : 'review'}</StatusChip></span>
              </div>
            ))}
          </div>
        </ConsolePanel>
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title={investigator.role === 'Principal Investigator' ? 'Studies led' : 'Studies supported'} muted={`${cov.supported.length} studies`} />
        <ConsoleTable>
          <thead><tr><th>Study</th><th>Status</th><th>Enrollment</th><th>Patients</th><th /></tr></thead>
          <tbody>{cov.supported.map((study) => <GoRow as="tr" go={`study/${study.code}`} key={study.code}><td><div className="nm">{study.name}</div><div className="pid">{study.code}</div></td><td><StatusChip tone={study.status}>{study.statusLbl}</StatusChip></td><td className="num">{study.enrolled}/{study.target}</td><td className="num">{patientsByStudy(study.code).length}</td><td className="t-r">›</td></GoRow>)}</tbody>
        </ConsoleTable>
      </ConsolePanel>
      <PatientTablePanel title="Patients under coverage" patients={cov.pts} empty="No patients in this scope" />
    </div>
  );
}

export function IntelligenceScreen() {
  const { signals, liveState, signalActions } = useConsoleModel();
  const signalFlagState = resolveFeatureFlagState('liveSignals', {
    loading: liveState.signalsLoading,
    error: liveState.signalsError,
    live: featureFlags.liveSignals && !liveState.signalsError,
    fallback: !featureFlags.liveSignals,
  });
  const backendSignalCopy = liveState.signalsMeta?.copy ?? 'Signals to investigate - not efficacy claims.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="rl-eyebrow" style={{ color: 'var(--violet)' }}><Icon name="target" />Signals to investigate</span>
        <Tier n={2} />
        {liveState.signalsLoading ? <StatusChip tone="acc">loading live signals</StatusChip> : null}
        <StatusChip tone={signalFlagState.state === 'live' ? 'pos' : signalFlagState.state === 'loading' ? 'acc' : 'warn'}>
          {signalFlagState.copy.label}
        </StatusChip>
      </div>
      <PreviewNote>{backendSignalCopy} Candidate signal. Outcome pattern. Requires review. Not an efficacy claim.</PreviewNote>
      {liveState.signalsActionError ? (
        <PreviewNote>{signalFlagState.copy.body}</PreviewNote>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="target" label="Active signals" value={String(signals.length)} detail="candidate review queue" />
        <Kpi icon="study" label="Cohorts compared" value="9" detail="1,284 patients" />
        <Kpi icon="lot" label="Strongest driver" value="Lot 4471" detail="candidate pattern" />
        <Kpi icon="measure" label="Mean confidence" value="0.89" detail="across signals" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Cohort response comparison" muted="6-month normalized response by cohort" />
          <div style={{ padding: '16px 18px 10px' }}>
            <LineChart
              labels={['Base', 'Wk2', 'Wk6', 'Wk12', 'Mo4', 'Mo5', 'Mo6']}
              series={[
                { name: 'Lot 4471 cohorts', color: 'var(--accent)', area: true, values: [0.2, 0.32, 0.48, 0.63, 0.75, 0.84, 0.89] },
                { name: 'Lot 4482 cohorts', color: 'var(--violet)', area: true, values: [0.2, 0.28, 0.39, 0.49, 0.57, 0.63, 0.67] },
              ]}
            />
            <div style={{ borderTop: '1px solid var(--hair)', marginTop: 14, paddingTop: 14 }}>
              <div className="rl-seclbl">Estimated outcome drivers</div>
              <ConsoleBarList>
                <ConsoleBarRow label="Lot / product line" value="0.41" width={82} color="var(--violet)" />
                <ConsoleBarRow label="Protocol adherence" value="0.28" width={58} />
                <ConsoleBarRow label="Time to procedure" value="0.17" width={36} />
                <ConsoleBarRow label="Indication severity" value="0.09" width={20} color="var(--faint)" />
              </ConsoleBarList>
            </div>
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Signals to investigate" muted="ranked by confidence - human-curated" right={<Tier n={2} />} />
          <div>
            {signals.map((signal) => (
              <GoRow className="rl-row" go={`signal/${signal.id}`} key={signal.id}>
                <span className="num" style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 600, color: 'var(--violet)', width: 20, flex: 'none' }}>{signal.rank}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}><div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35, whiteSpace: 'normal' }}>{signal.title}</div><StatusChip tone={signal.status === 'reviewed' ? 'pos' : signal.status === 'dismissed' ? 'neg' : 'warn'}>{signal.status ?? 'candidate'}</StatusChip></div>
                  <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6, lineHeight: 1.45, whiteSpace: 'normal' }}>{signal.desc}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 9 }}><Progress value={signal.conf} color="var(--violet)" style={{ width: 100 }} /><span className="num" style={{ fontSize: 10.5, color: 'var(--violet)' }}>conf {(signal.conf / 100).toFixed(2)}</span><span className="num" style={{ fontSize: 10.5, color: 'var(--faint)' }}>{signal.n ? `n=${signal.n}` : 'live'}</span></div>
                </div>
                {featureFlags.liveSignals ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ConsoleButton
                      variant="gho"
                      disabled={signalActions.reviewingId === signal.id || signal.status === 'reviewed'}
                      onClick={(event) => {
                        event.stopPropagation();
                        void signalActions.review(signal.id);
                      }}
                    >
                      {signalActions.reviewingId === signal.id ? 'Reviewing' : 'Mark reviewed'}
                    </ConsoleButton>
                    <ConsoleButton
                      variant="ghost-quiet"
                      disabled={signalActions.dismissingId === signal.id || signal.status === 'dismissed'}
                      onClick={(event) => {
                        event.stopPropagation();
                        void signalActions.dismiss(signal.id);
                      }}
                    >
                      {signalActions.dismissingId === signal.id ? 'Dismissing' : 'Dismiss'}
                    </ConsoleButton>
                  </div>
                ) : null}
              </GoRow>
            ))}
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--hair)', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}>
            Signals are associations surfaced for review. Not an efficacy claim.
          </div>
        </ConsolePanel>
      </div>
    </div>
  );
}

export function SignalDetailScreen() {
  const { id } = useParams();
  const { signals, liveState, signalActions } = useConsoleModel();
  const signal = signals.find((item) => item.id === id) ?? signalById(id) ?? prototypeSignals[0]!;
  const lot = signal.lot ? lotById(signal.lot) : null;
  const study = studyByCode(signal.study);
  const signalFlagState = resolveFeatureFlagState('liveSignals', {
    loading: liveState.signalsLoading,
    error: liveState.signalsError,
    live: featureFlags.liveSignals && !liveState.signalsError,
    fallback: !featureFlags.liveSignals,
  });
  const signalStatusTone = signal.status === 'reviewed' ? 'pos' : signal.status === 'dismissed' ? 'neg' : 'vio';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <Tile icon="target" color="var(--violet)" size={46} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="h-title">{signal.title}</span><StatusChip tone={signalStatusTone}>{signal.status ?? 'candidate'} signal #{signal.rank}</StatusChip><Tier n={2} /></div>
          <div className="h-meta">{signal.driver} - Study {signal.study} - {signal.n ? `n=${signal.n}` : 'live review queue'}</div>
        </div>
        <div className="h-stats">
          <div><div className="v num" style={{ color: 'var(--violet)' }}>{(signal.conf / 100).toFixed(2)}</div><div className="l">Confidence</div></div>
          <div><div className="v num">{signal.n || '-'}</div><div className="l">Sample size</div></div>
          <div><div className="v num">#{signal.rank}</div><div className="l">Rank</div></div>
        </div>
      </ConsolePanel>
      <div className="rl-pillrow">
        <LinkPill to={`/console/studies/${signal.study}`} color="var(--accent)">Study {signal.study} {study?.name}</LinkPill>
        {lot ? <LinkPill to={`/console/lots/${lot.id}`} color="var(--labs)">Lot #{lot.id} {lot.line}</LinkPill> : null}
        <LinkPill to="/console/intelligence" color="var(--violet)">All signals</LinkPill>
      </div>
      {featureFlags.liveSignals ? (
        <ConsolePanel style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}>
            {signalFlagState.copy.body} Candidate signal. Outcome pattern. Requires review. Not an efficacy claim.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ConsoleButton
              variant="gho"
              disabled={signalActions.reviewingId === signal.id || signal.status === 'reviewed'}
              onClick={() => {
                void signalActions.review(signal.id);
              }}
            >
              {signalActions.reviewingId === signal.id ? 'Reviewing' : 'Mark reviewed'}
            </ConsoleButton>
            <ConsoleButton
              variant="ghost-quiet"
              disabled={signalActions.dismissingId === signal.id || signal.status === 'dismissed'}
              onClick={() => {
                void signalActions.dismiss(signal.id);
              }}
            >
              {signalActions.dismissingId === signal.id ? 'Dismissing' : 'Dismiss'}
            </ConsoleButton>
          </div>
        </ConsolePanel>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="What the data shows" muted="Finding" right={<span className="rl-link"><Icon name="book" />Citations</span>} />
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p className="rl-prose">{signal.detail}</p>
            <LineChart
              height={180}
              labels={['Base', 'Wk2', 'Wk6', 'Wk12', 'Mo4', 'Mo6']}
              series={[
                { name: signal.driver, color: 'var(--violet)', area: true, values: [0.2, 0.33, 0.5, 0.65, 0.78, signal.conf / 100] },
                { name: 'Comparison', color: 'var(--faint)', dashed: true, values: [0.2, 0.28, 0.38, 0.48, 0.56, 0.62] },
              ]}
            />
          </div>
        </ConsolePanel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ConsolePanel>
            <ConsolePanelHeader title="Confidence" />
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <ConsoleRing value={(signal.conf / 100).toFixed(2)} label="confidence" />
              <div style={{ fontSize: 11, color: 'var(--faint)', textAlign: 'center', fontFamily: 'var(--mono)' }}>{signal.n ? `based on n=${signal.n} patients` : 'live queue value'}</div>
            </div>
          </ConsolePanel>
          <ConsolePanel>
            <ConsolePanelHeader title="Attributes" />
            <div style={{ padding: '8px 18px 16px' }}>
              <dl className="rl-kv">
                <dt>Primary driver</dt><dd>{signal.driver}</dd>
                <dt>Scope</dt><dd>Study {signal.study}</dd>
                <dt>Sample size</dt><dd className="num">{signal.n ? `n=${signal.n}` : 'live'}</dd>
                <dt>Status</dt><dd>{signal.status ?? 'candidate'} - requires review</dd>
              </dl>
            </div>
          </ConsolePanel>
        </div>
      </div>
      <SafetyDisclaimer />
    </div>
  );
}

export function CompareScreen() {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('lots')?.split(',').filter(Boolean);
  const lotIds = requested?.length ? requested.slice(0, 3) : ['4471', '4482', '4455'];
  const compareLots = lotIds.map((id) => lotById(id)).filter((lot): lot is LotLineage => Boolean(lot));
  const panels = compareLots.map((lot) => panelFor(lot.id));
  const markerMaps = panels.map((panel) => new Map(panel.markers.map((marker) => [marker.name, marker])));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="rl-pillrow">
        {compareLots.map((lot) => <LinkPill key={lot.id} to={`/console/lots/${lot.id}`} color="var(--labs)">Lot #{lot.id} - {lot.line}</LinkPill>)}
        <LinkPill to="/console/lots" color="var(--accent)">Change lots</LinkPill>
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title="Unified marker grid" muted={`${compareLots.length} lots - release and characterization, aligned`} right={<Tier n={1} />} />
        <table className="rl-cmp">
          <thead>
            <tr>
              <th>Marker</th>
              {compareLots.map((lot) => <th key={lot.id}><div className="lothd"><span className="id">#{lot.id}</span><span className="ln">{lot.line} - {lot.type}</span></div></th>)}
              <th>Reference</th>
            </tr>
          </thead>
          <tbody>
            {compareRows.map((row, rowIndex) => {
              if (row.section) {
                return <tr className="section" key={row.section}><td colSpan={compareLots.length + 2}>{row.section}</td></tr>;
              }
              const cells = markerMaps.map((map) => map.get(row.key ?? ''));
              const ref = cells.find(Boolean);
              return (
                <tr key={`${row.key}-${rowIndex}`}>
                  <td className="mkr">{row.key}<small>{ref?.sub}</small></td>
                  {cells.map((cell, index) => (
                    <td className="cell" key={`${row.key}-${compareLots[index]?.id}`}>
                      {cell ? <div className="v"><span style={{ color: cell.finding === 'fail' ? 'var(--neg)' : cell.finding === 'warn' ? 'var(--warn)' : 'var(--pos)' }}>{cell.value}</span><span className={`rl-prov ${cell.prov}`}><span className="pd" />{cell.prov}</span></div> : <span style={{ color: 'var(--faint)' }}>-</span>}
                    </td>
                  ))}
                  <td className="refcol">{ref?.refText ?? '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: '13px 16px', borderTop: '1px solid var(--hair)' }}>
          <div className="rl-provlegend">
            <span className="rl-prov measured"><span className="pd" />measured</span>
            <span className="rl-prov imported"><span className="pd" />imported</span>
            <span className="rl-prov reference"><span className="pd" />reference</span>
          </div>
        </div>
      </ConsolePanel>
      <ConsolePanel className="rl-preview rl-inert" style={{ display: 'flex', flexDirection: 'column', minHeight: 220 }}>
        <div className="rl-preview-ribbon">PREVIEW</div>
        <ConsolePanelHeader title="Outcome overlay" muted="6-month response by lot, on the same grid" right={<Tier n={2} />} />
        <div style={{ padding: '18px 20px' }}>
          <PreviewNote>Once longitudinal outcomes are captured, this grid extends with outcome-pattern rows. It stays inert until outcomes data exists.</PreviewNote>
        </div>
        <div className="rl-inert-veil"><span className="tag">Inert until outcomes data exists</span></div>
      </ConsolePanel>
      <SafetyDisclaimer />
    </div>
  );
}

export function ConsentAccessScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="shield" label="Active consents" value="1,350" detail="patient-authorized" />
        <Kpi icon="check" label="Consent rate" value="93" suffix="%" detail="of eligible" />
        <Kpi icon="provider" label="Data partners" value="6" detail="with active grants" />
        <Kpi icon="clock" label="Pending review" value="4" detail="access requests" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Data access grants" muted="who can see what" />
          <div>
            {[
              ['var(--labs)', 'Lab partners', 'De-identified outcomes for linked lots', 'Granted', 'pos'],
              ['var(--phy)', 'Provider sites', 'Their own patients - full record', 'Granted', 'pos'],
              ['var(--pat)', 'Patients', 'Own record and study summary', 'Granted', 'pos'],
              ['var(--accent)', 'Research analysts', 'De-identified cross-study', 'Granted', 'pos'],
              ['var(--warn)', 'External registry', 'Aggregate outcomes only', 'Pending', 'warn'],
            ].map(([color, who, scope, status, tone]) => (
              <div className="rl-row" key={String(who)}>
                <span className="r-ic" style={{ background: `color-mix(in oklab, ${color}, transparent 84%)` }}><Icon name="shield" /></span>
                <div><div className="r-t">{who}</div><div className="r-s">{scope}</div></div>
                <span className="r-r"><StatusChip tone={tone as StatusTone}>{status}</StatusChip></span>
              </div>
            ))}
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Consent ledger" muted="recent activity" />
          <div>
            {([
              ['Consent signed - P-2048', 'Study KOA-3 - full participation', '2h'],
              ['Access granted', 'Summit Regen - site data', '5h'],
              ['Consent renewed - P-1991', 'Annual re-consent', '1d'],
              ['Request pending', 'External registry - aggregate', '1d'],
              ['Consent signed - P-2041', 'Study RCR-2', '2d'],
            ] as const).map(([title, sub, time]) => (
              <div className="rl-row" key={title}><span className="r-ic"><Icon name={title.includes('pending') ? 'clock' : 'check'} /></span><div><div className="r-t">{title}</div><div className="r-s">{sub}</div></div><span className="r-r num" style={{ fontSize: 11, color: 'var(--faint)' }}>{time}</span></div>
            ))}
          </div>
        </ConsolePanel>
      </div>
      <ConsolePanel style={{ padding: '14px 18px', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}>
        Every data flow in the loop is consent-gated and logged. Patients can revoke access at any time from the patient app.
      </ConsolePanel>
    </div>
  );
}

const auditTabs = [
  ['all', 'All'],
  ['access', 'Access'],
  ['release', 'Lot release'],
  ['consent', 'Consent'],
  ['ae', 'Adverse events'],
  ['export', 'Exports'],
  ['auth', 'Sign-in'],
] as const;

export function AuditScreen() {
  const { audit } = useConsoleModel();
  const [params] = useSearchParams();
  const filter = params.get('filter') ?? 'all';
  const rows = audit.filter((entry) => filter === 'all' || entry.kind === filter);
  const flagged = audit.filter((entry) => entry.status === 'flag').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="history" label="Events today" value={String(audit.length)} detail="append-only trail" />
        <Kpi icon="alert" label="Flagged" value={String(flagged)} detail="requires review" />
        <Kpi icon="download" label="Exports" value={String(audit.filter((entry) => entry.kind === 'export').length)} detail="last 7 days" />
        <Kpi icon="eye" label="Access events" value={String(audit.filter((entry) => entry.kind === 'access').length)} detail="attributed users" />
      </div>
      <ConsoleTabs style={{ flexWrap: 'wrap' }}>
        {auditTabs.map(([key, label]) => <Link className={`rl-tab ${key === filter ? 'on' : ''}`} to={key === 'all' ? '/console/audit' : `/console/audit?filter=${key}`} key={key}>{label}</Link>)}
      </ConsoleTabs>
      <ConsolePanel>
        <ConsolePanelHeader title="Activity trail" muted="every action, attributed" right={<ConsoleButton variant="gho"><Icon name="download" />Export log</ConsoleButton>} />
        <ConsoleTable>
          <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Kind</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((entry) => (
              <GoRow as="tr" go={entry.go} key={entry.id}>
                <td className="num">{entry.at}</td>
                <td><div className="rl-cell-id"><MiniAvatar initials={entry.initials} /><div><div className="nm">{entry.actor}</div><div className="pid">{entry.role}</div></div></div></td>
                <td>{entry.verb}</td>
                <td>{entry.target}</td>
                <td><StatusChip tone={entry.kind === 'ae' ? 'neg' : entry.kind === 'release' ? 'acc' : undefined}>{entry.kind}</StatusChip></td>
                <td><StatusChip tone={entry.status === 'flag' ? 'warn' : 'pos'}>{entry.status}</StatusChip></td>
              </GoRow>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

const aeTabs = [
  ['all', 'All'],
  ['open', 'Open'],
  ['serious', 'Serious'],
  ['closed', 'Closed'],
] as const;

function filterAes(filter: string) {
  if (filter === 'open') return adverseEvents.filter((event) => event.status !== 'Closed');
  if (filter === 'serious') return adverseEvents.filter((event) => event.serious);
  if (filter === 'closed') return adverseEvents.filter((event) => event.status === 'Closed');
  return adverseEvents;
}

export function AdverseEventsScreen() {
  const [params] = useSearchParams();
  const filter = params.get('filter') ?? 'all';
  const rows = filterAes(filter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
        <Kpi icon="measure" label="Total logged" value={String(adverseEvents.length)} detail="across all studies" />
        <Kpi icon="alert" label="Open review" value={String(adverseEvents.filter((event) => event.status !== 'Closed').length)} detail="active safety desk" />
        <Kpi icon="shield" label="Serious" value={String(adverseEvents.filter((event) => event.serious).length)} detail="expedited clock" />
        <Kpi icon="clock" label="Reportable" value={String(adverseEvents.filter((event) => event.reportable && event.status !== 'Closed').length)} detail="pending timeline" />
      </div>
      <ConsoleTabs>
        {aeTabs.map(([key, label]) => <Link className={`rl-tab ${key === filter ? 'on' : ''}`} to={key === 'all' ? '/console/adverse-events' : `/console/adverse-events?filter=${key}`} key={key}>{label}</Link>)}
      </ConsoleTabs>
      <ConsolePanel>
        <ConsolePanelHeader title="Event registry" muted="safety surveillance" right={<span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--faint)' }}>Every event is audit-logged and lot-linked</span>} />
        <ConsoleTable>
          <thead><tr><th>Event</th><th>Patient</th><th>Lot</th><th>Severity</th><th>Relatedness</th><th>Status</th><th>Due</th><th /></tr></thead>
          <tbody>
            {rows.map((event) => (
              <GoRow as="tr" go={`ae/${event.id}`} key={event.id}>
                <td><div className="nm">{event.term}</div><div className="pid">{event.id} - {event.system}</div></td>
                <td><div className="rl-cell-id"><MiniAvatar initials={event.initials ?? '--'} /><div><div className="nm">{event.name}</div><div className="pid">{event.pid}</div></div></div></td>
                <td><Link className="rl-link num" to={`/console/lots/${event.lot}`}>#{event.lot}</Link></td>
                <td><StatusChip tone={event.severity === 'Severe' ? 'neg' : event.severity === 'Moderate' ? 'warn' : 'pos'}>{event.severity}{event.serious ? ' - serious' : ''}</StatusChip></td>
                <td>{event.relatedness}</td>
                <td><StatusChip tone={event.status === 'Closed' ? 'pos' : 'acc'}>{event.status}</StatusChip></td>
                <td className="num">{event.due || '-'}</td>
                <td className="t-r">›</td>
              </GoRow>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

export function AdverseEventDetailScreen() {
  const { id } = useParams();
  const event = aeById(id) ?? adverseEvents[0]!;
  const patient = patientById(event.pid);
  const provider = providerById(event.provider);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsolePanel className="rl-ehead">
        <Tile icon="alert" color="var(--neg)" size={46} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><span className="h-title">{event.term}</span><StatusChip tone={event.serious ? 'neg' : 'warn'}>{event.severity}{event.serious ? ' - serious' : ''}</StatusChip></div>
          <div className="h-meta">{event.id} - {event.system} - onset {event.onset} - {event.daysFromDose} days from dose</div>
        </div>
        <div className="h-stats">
          <div><div className="v num">{event.daysFromDose}</div><div className="l">Days from dose</div></div>
          <div><div className="v num">{event.reportable ? 'Yes' : 'No'}</div><div className="l">Reportable</div></div>
          <div><div className="v num">{event.status}</div><div className="l">Status</div></div>
        </div>
      </ConsolePanel>
      <div className="rl-pillrow">
        <LinkPill to={`/console/outcomes-registry/${event.pid}`} color="var(--pat)">{patient?.name ?? event.pid}</LinkPill>
        <LinkPill to={`/console/lots/${event.lot}`} color="var(--labs)">Lot #{event.lot}</LinkPill>
        {provider ? <LinkPill to={`/console/providers/${provider.id}`} color="var(--phy)">{provider.name}</LinkPill> : null}
        <LinkPill to="/console/adverse-events" color="var(--neg)">All events</LinkPill>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Narrative" muted="safety record" />
          <div style={{ padding: '18px 20px' }}><p className="rl-prose">{event.narrative}</p></div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Classification" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, padding: 14 }}>
            {attr('Expectedness', event.expectedness)}
            {attr('Relatedness', event.relatedness)}
            {attr('Outcome', event.outcome)}
            {attr('Due', event.due || '-')}
          </div>
        </ConsolePanel>
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title="Case timeline" muted="audit-linked" />
        <div>
          {event.actions.map(([date, actor, action]) => (
            <div className="rl-row" key={`${date}-${action}`}>
              <span className="r-ic"><Icon name="history" /></span>
              <div><div className="r-t">{action}</div><div className="r-s">{actor}</div></div>
              <span className="r-r num">{date}</span>
            </div>
          ))}
        </div>
      </ConsolePanel>
      <ConsolePanel style={{ padding: '14px 18px', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--faint)', letterSpacing: '0.01em' }}>
        Safety record - audit-logged - research and quality-support. Not a regulatory submission system.
      </ConsolePanel>
    </div>
  );
}

const roles = [
  ['Administrator', 'var(--accent)', 'Manages members, access policy and workspace configuration. Full data access.', true],
  ['Principal Investigator', 'var(--violet)', 'Owns studies and signs off clinical data across their team. Full clinical access.', true],
  ['Research Coordinator', 'var(--pat)', 'Enrolls patients, schedules follow-ups and records outcomes at assigned sites. No sign-off.', true],
  ['Lab Operator', 'var(--labs)', 'Sources cell lines, produces lots and logs chain-of-custody. No patient PHI.', false],
  ['QA / Lot Release', 'var(--warn)', 'Reviews QC and releases lots. Separation of duties applies.', false],
  ['Analyst', 'var(--pos)', 'De-identified registry and signal review, read-only. No PHI.', false],
] as const;

const team = [
  { in: 'MO', nm: 'Dr. Mara Okonkwo', em: 'm.okonkwo@regenloop.io', role: 'Principal Investigator', scope: 'All studies', last: 'Active now', status: 'pos', lbl: 'Active' },
  { in: 'EN', nm: 'Elena Novak', em: 'e.novak@regenloop.io', role: 'Administrator', scope: 'Workspace', last: '30m ago', status: 'pos', lbl: 'Active' },
  { in: 'PR', nm: 'Priya Raman', em: 'p.raman@regenloop.io', role: 'Research Coordinator', scope: '3 sites', last: '18m ago', status: 'pos', lbl: 'Active' },
  { in: 'DC', nm: 'Daniel Cho', em: 'd.cho@regenloop.io', role: 'Lab Operator', scope: 'Cell-line lab', last: '2h ago', status: 'pos', lbl: 'Active' },
  { in: 'ML', nm: 'Maya Lindqvist', em: 'm.lindqvist@regenloop.io', role: 'Lab Operator', scope: 'Production floor', last: '-', status: 'warn', lbl: 'Invited' },
] as const;

function SettingRow({ title, subtitle, on = true }: { title: string; subtitle: string; on?: boolean }) {
  return <div className="rl-setrow"><div><div className="st-t">{title}</div><div className="st-s">{subtitle}</div></div><div className={`rl-toggle ${on ? 'on' : ''}`} /></div>;
}

export function SettingsScreen() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsoleTabs>
        <Link className="rl-tab on" to="/console/settings">Workspace</Link>
        <Link className="rl-tab" to="/console/settings/team-access">Team and access</Link>
      </ConsoleTabs>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Workspace settings" muted="RegenLoop Lab Console" />
          <SettingRow title="Lot variability flags" subtitle="Flag lots tracking below study mean" />
          <SettingRow title="Outcome review queue" subtitle="Require qualified review before marking a signal reviewed" />
          <SettingRow title="Consent-gated exports" subtitle="Strip PHI unless explicitly authorized" />
          <SettingRow title="AI evidence drawer" subtitle="Require citation metadata for co-pilot findings" />
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Data and privacy" />
          <div style={{ padding: '18px 20px' }}>
            <dl className="rl-kv">
              <dt>Workspace</dt><dd>Northgate Regenerative Institute</dd>
              <dt>Region</dt><dd>US</dd>
              <dt>Default access</dt><dd>Least privilege</dd>
              <dt>Export mode</dt><dd>De-identified by default</dd>
            </dl>
          </div>
        </ConsolePanel>
      </div>
      <SafetyDisclaimer />
    </div>
  );
}

export function TeamAccessScreen() {
  const roleColor = (role: string) => roles.find((item) => item[0] === role)?.[1] ?? 'var(--faint)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsoleTabs>
        <Link className="rl-tab" to="/console/settings">Workspace</Link>
        <Link className="rl-tab on" to="/console/settings/team-access">Team and access</Link>
      </ConsoleTabs>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConsolePanel>
          <ConsolePanelHeader title="Invite teammate" muted="least-privilege defaults" />
          <div style={{ padding: 18, display: 'grid', gap: 12 }}>
            <input className="rl-input" placeholder="name@regenloop.io" aria-label="Invite email" />
            <select className="rl-select" aria-label="Role">
              {roles.map(([role]) => <option key={role}>{role}</option>)}
            </select>
            <select className="rl-select" aria-label="Access scope">
              <option>All studies and sites</option>
              <option>Specific studies</option>
              <option>Specific sites</option>
              <option>De-identified only</option>
            </select>
            <ConsoleButton variant="pri"><Icon name="plus" />Invite teammate</ConsoleButton>
          </div>
          <div style={{ padding: '0 18px 15px', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--faint)', lineHeight: 1.6 }}>
            Access is least-privilege by default. PHI is only exposed to roles that require it, and every grant is logged.
          </div>
        </ConsolePanel>
        <ConsolePanel>
          <ConsolePanelHeader title="Role permissions" />
          <div>
            {roles.map(([role, color, blurb, phi]) => (
              <div className="rl-row" key={role}>
                <span className="r-ic" style={{ color }}><Icon name="shield" /></span>
                <div><div className="r-t">{role}</div><div className="r-s">{blurb}</div></div>
                <span className="r-r"><StatusChip tone={phi ? 'acc' : 'pos'}>{phi ? 'PHI' : 'No PHI'}</StatusChip></span>
              </div>
            ))}
          </div>
        </ConsolePanel>
      </div>
      <ConsolePanel>
        <ConsolePanelHeader title="Members" muted={`${team.filter((member) => member.lbl === 'Active').length} active - ${team.filter((member) => member.lbl !== 'Active').length} pending`} />
        <ConsoleTable>
          <thead><tr><th>Member</th><th>Role</th><th>Scope</th><th>Last active</th><th>Status</th><th /></tr></thead>
          <tbody>
            {team.map((member) => (
              <tr key={member.em}>
                <td><div className="rl-cell-id"><MiniAvatar initials={member.in} /><div><div className="nm">{member.nm}</div><div className="pid">{member.em}</div></div></div></td>
                <td><StatusChip><span className="d" style={{ background: roleColor(member.role) }} />{member.role}</StatusChip></td>
                <td>{member.scope}</td>
                <td className="num">{member.last}</td>
                <td><StatusChip tone={member.status as StatusTone}>{member.lbl}</StatusChip></td>
                <td className="t-r"><ConsoleButton variant="gho">Review</ConsoleButton></td>
              </tr>
            ))}
          </tbody>
        </ConsoleTable>
      </ConsolePanel>
    </div>
  );
}

export function ConsoleRoutePlaceholder({
  title = 'Console surface unavailable',
  description = 'This console route has no mapped prototype screen yet.',
  bullets = [],
}: {
  title?: string;
  description?: string;
  bullets?: string[];
}) {
  const location = useLocation();
  const pathname = location.pathname;

  if (pathname === '/console/providers') return <ProvidersScreen />;
  if (pathname.startsWith('/console/providers/')) return <ProviderDetailScreen />;
  if (pathname === '/console/investigators') return <InvestigatorsScreen />;
  if (pathname.startsWith('/console/investigators/')) return <InvestigatorDetailScreen />;
  if (pathname.startsWith('/console/intelligence/signals/')) return <SignalDetailScreen />;
  if (pathname === '/console/consent-access') return <ConsentAccessScreen />;
  if (pathname === '/console/settings/team-access') return <TeamAccessScreen />;

  return (
    <ConsolePanel>
      <ConsolePanelHeader title={title} muted={pathname} />
      <div className="rl-empty">
        <Icon name="search" />
        <div>{description}</div>
        {bullets.length ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {bullets.map((bullet) => (
              <ConsoleChip key={bullet}>{bullet}</ConsoleChip>
            ))}
          </div>
        ) : null}
      </div>
    </ConsolePanel>
  );
}
